import { useEffect, useState } from "react";

import { ValueSetExpansionContains } from "@medplum/fhirtypes";
import { useMedplum } from "@medplum/react";

import { Checkbox, Group, NativeSelect, NumberInput, Stack, Text, Textarea } from "@mantine/core";
import type { UseFormReturnType } from "@mantine/form";

import { ClearableYesNoRadioField, YES_DISPLAY } from "@/components/ClearableYesNoRadioField";
import { BaselineFhr } from "@/encounters/components/EncounterLabor/EncounterLabor/LaborFlowsheetComponents/BaselineFHR";
import { FETAL_HEART_RATE_DATE_TIME_LINK_ID } from "@/encounters/components/EncounterLabor/flowsheet-forms/fetal-heart-rate/constants/fetal-heart-rate-link-ids";
import type { FetalHeartRateFlowsheetFormValues } from "@/encounters/components/EncounterLabor/flowsheet-forms/fetal-heart-rate/types/fetal-heart-rate-form";
import { valueSetContainsToNativeSelectOptions } from "@/encounters/components/EncounterLabor/flowsheet-forms/shared/labor-flowsheet-coding-select";
import { LaborFlowsheetDateTimeFields } from "@/encounters/components/EncounterLabor/flowsheet-forms/shared/LaborFlowsheetDateTimeFields";
import { LaborFlowsheetSignatureInitialsField } from "@/encounters/components/EncounterLabor/flowsheet-forms/shared/LaborFlowsheetSignatureInitialsField";
import { QuiltedHealthValueSets } from "@/fhir/enums/quilted-health";
import {
  questionnaireCheckboxOptionTestId,
  questionnaireFieldTestId,
} from "@/intake-forms/utils/questionnaire-field-testid";
import { getValueSetOptions } from "@/utils/medplum";
import { toast } from "@/utils/notifications";

interface FetalHeartRateFormFieldsProps {
  form: UseFormReturnType<FetalHeartRateFlowsheetFormValues>;
  clientId: string;
  encounterId: string;
  mode?: "quick-add" | "edit";
  isDrawerOpened?: boolean;
  baselineRefreshKey?: number;
}

export function FetalHeartRateFormFields({
  form,
  clientId,
  encounterId,
  mode = "quick-add",
  isDrawerOpened = true,
  baselineRefreshKey = 0,
}: FetalHeartRateFormFieldsProps) {
  const medplum = useMedplum();
  const [listeningDeviceOptions, setListeningDeviceOptions] = useState<ValueSetExpansionContains[]>(
    [],
  );
  const [relationToContractionsOptions, setRelationToContractionsOptions] = useState<
    ValueSetExpansionContains[]
  >([]);
  const listeningDeviceInputProps = form.getInputProps("fhrListeningDevice");

  const assessBaselineIsYes = form.values.assessBaseline === YES_DISPLAY;

  useEffect(() => {
    if (!isDrawerOpened) {
      return;
    }

    let isCancelled = false;

    const loadValueSets = async () => {
      try {
        const [listeningDevice, relationToContractions] = await Promise.all([
          getValueSetOptions(medplum, QuiltedHealthValueSets.ListeningDevice),
          getValueSetOptions(medplum, QuiltedHealthValueSets.RelationToContractions),
        ]);
        if (isCancelled) {
          return;
        }
        setListeningDeviceOptions(listeningDevice ?? []);
        setRelationToContractionsOptions(relationToContractions ?? []);
      } catch (error) {
        console.error("Error loading fetal heart rate value sets:", error);
        if (!isCancelled) {
          toast({
            type: "error",
            title: "Failed to load form options",
            message:
              "Listening device and relation to contractions options could not be loaded. Try closing and reopening the drawer.",
            autoClose: false,
          });
        }
      }
    };

    void loadValueSets();

    return () => {
      isCancelled = true;
    };
  }, [isDrawerOpened, medplum]);

  const relationCheckboxValues =
    form.values.fhrRelationToContractions?.map((item) => {
      if (typeof item === "string") {
        return item;
      }
      return JSON.stringify(item);
    }) ?? [];

  return (
    <Stack gap="lg">
      <LaborFlowsheetDateTimeFields
        form={form}
        dateFieldName="fhrDate"
        timeFieldName="fhrTime"
        autoPopulateDateTime={mode === "quick-add"}
        dateTimeGroupLinkId={FETAL_HEART_RATE_DATE_TIME_LINK_ID}
      />

      <BaselineFhr
        encounterId={encounterId}
        refreshWhen={isDrawerOpened}
        baselineRefreshKey={baselineRefreshKey}
      />

      <Stack gap="xs">
        <Text fw={500} size="sm">
          Beats Per Minute
        </Text>
        <Group grow align="flex-end">
          <NumberInput
            label="Low"
            clampBehavior="strict"
            data-path="fhrBPM.fhrBPMLow"
            {...form.getInputProps("fhrBPMLow")}
            data-testid={questionnaireFieldTestId("fhrBPM.fhrBPMLow")}
          />
          <NumberInput
            label="High"
            clampBehavior="strict"
            data-path="fhrBPM.fhrBPMHigh"
            {...form.getInputProps("fhrBPMHigh")}
            data-testid={questionnaireFieldTestId("fhrBPM.fhrBPMHigh")}
          />
        </Group>
      </Stack>

      <NumberInput
        clampBehavior="strict"
        rightSectionWidth={70}
        label="Duration of Listening"
        data-path="fhrDurationOfListening"
        rightSection={<Text size="sm">Seconds</Text>}
        {...form.getInputProps("fhrDurationOfListening")}
        data-testid={questionnaireFieldTestId("fhrDurationOfListening")}
      />

      <NativeSelect
        label="Listening Device"
        data-path="fhrListeningDevice"
        data={valueSetContainsToNativeSelectOptions(listeningDeviceOptions)}
        {...listeningDeviceInputProps}
        value={listeningDeviceInputProps.value ?? ""}
        data-testid={questionnaireFieldTestId("fhrListeningDevice")}
      />

      <Checkbox.Group
        value={relationCheckboxValues}
        label="Relation to Contractions"
        data-testid={questionnaireFieldTestId("fhrRelationToContractions")}
        onChange={(values) => form.setFieldValue("fhrRelationToContractions", values)}
      >
        <Stack mt="sm">
          {relationToContractionsOptions.map((option) => (
            <Checkbox
              key={option.code}
              label={option.display}
              value={JSON.stringify({
                system: option.system,
                code: option.code,
                display: option.display,
              })}
              data-testid={
                option.display
                  ? questionnaireCheckboxOptionTestId("fhrRelationToContractions", option.display)
                  : undefined
              }
            />
          ))}
        </Stack>
      </Checkbox.Group>

      <ClearableYesNoRadioField
        form={form}
        name="accelerationsDetected"
        label="Accelerations Detected"
        dataPath="accelerationsDetected"
        dataTestId={questionnaireFieldTestId("accelerationsDetected")}
      />

      <ClearableYesNoRadioField
        form={form}
        name="decelerationsDetected"
        label="Decelerations Detected"
        dataPath="decelerationsDetected"
        dataTestId={questionnaireFieldTestId("decelerationsDetected")}
      />

      <ClearableYesNoRadioField
        form={form}
        name="assessBaseline"
        label="Assess Baseline"
        dataPath="assessBaseline"
        dataTestId={questionnaireFieldTestId("assessBaseline")}
        onClear={() => {
          form.setFieldValue("fhrBaseline", "");
          form.setFieldValue("fhrDurationOfListeningBaseline", "");
        }}
      />

      {assessBaselineIsYes ? (
        <Stack gap="md">
          <NumberInput
            label="Baseline FHR"
            clampBehavior="strict"
            rightSectionWidth={50}
            rightSection={<Text size="sm">BPM</Text>}
            {...form.getInputProps("fhrBaseline")}
            data-testid={questionnaireFieldTestId("fhrBaseline")}
          />
          <NumberInput
            clampBehavior="strict"
            rightSectionWidth={70}
            label="Duration of Listening"
            rightSection={<Text size="sm">Seconds</Text>}
            {...form.getInputProps("fhrDurationOfListeningBaseline")}
            data-testid={questionnaireFieldTestId("fhrDurationOfListeningFHT")}
          />
        </Stack>
      ) : null}

      <Textarea
        label="Notes"
        data-path="fhrNotes"
        {...form.getInputProps("fhrNotes")}
        data-testid={questionnaireFieldTestId("fhrNotes")}
      />

      <LaborFlowsheetSignatureInitialsField
        form={form}
        clientId={clientId}
        label="Sign Initials"
        name="fhrSignatureInitials"
        autoPopulateSignature={mode === "quick-add"}
      />
    </Stack>
  );
}
